import type { ChatMessageAttachment } from '@/slices/chat/chatSlice';
import type { ChatTurnAttachment } from '@/types/chatStream';

export const IMAGE_RETRY_DISABLED_REASON =
  "Attachments from this message can't be re-sent";

export const IMAGE_RETRY_TEXT_ONLY_NOTICE =
  "Some attachments from this message can't be re-sent. Retrying the remaining content.";

const toTurnAttachment = (
  attachment: ChatMessageAttachment
): ChatTurnAttachment | null =>
  typeof attachment.key === 'string' &&
  attachment.key !== '' &&
  attachment.mimeType !== '' &&
  typeof attachment.sizeBytes === 'number' &&
  Number.isInteger(attachment.sizeBytes) &&
  attachment.sizeBytes > 0
    ? {
        key: attachment.key,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
      }
    : null;

/** Decide whether retry can faithfully replay a message with its attachments. */
export const planAttachmentRetry = (message: {
  text: string;
  attachments: ChatMessageAttachment[];
  documentIds?: string[];
}) => {
  const imageAttachments = message.attachments.filter(
    (attachment) => attachment.kind !== 'document'
  );

  const projected = imageAttachments.map(toTurnAttachment);

  const hasIncompleteAttachment = projected.some(
    (attachment) => attachment === null
  );

  const hasText = message.text.trim() !== '';

  const attachments = hasIncompleteAttachment
    ? []
    : projected.flatMap((attachment) => (attachment ? [attachment] : []));

  const documentIds = Array.from(
    new Set([
      ...(message.documentIds ?? []),
      ...message.attachments.flatMap((attachment) =>
        attachment.kind === 'document' && attachment.documentId
          ? [attachment.documentId]
          : []
      ),
    ])
  );

  const canReplayOtherContent =
    hasText || attachments.length > 0 || documentIds.length > 0;

  return {
    attachments,
    documentIds,
    canRetry: canReplayOtherContent,
    disabledReason:
      hasIncompleteAttachment && !canReplayOtherContent
        ? IMAGE_RETRY_DISABLED_REASON
        : null,
    notice:
      hasIncompleteAttachment && canReplayOtherContent
        ? IMAGE_RETRY_TEXT_ONLY_NOTICE
        : null,
  };
};
